/**
 * The quote draft: the strip (caption, count badges, "Show details"), then the lines as
 * a table (a laptop) or as cards (a phone, the Outlook pane), in triage order: what
 * needs the sales person, then what is matched, then what is not quoted (folded).
 *
 * The caption counts what I2Q found ("3 of 4 found") and the requests the customer
 * removed ("1 request removed"); those rows and the stand-in lines (the whole mail) are
 * hidden. "Show details" is remembered in this browser (`i2q.case.details`) and off by
 * default: in the table it puts the long figures in the cells and the order value line
 * under the table; in the list it opens every card. A case without lines says why in
 * one sentence.
 *
 * Lines sales took out of the quotation keep their "Put back" under the list; "Add line"
 * sits under "More". Under them, "Named in the earlier mails" lists the parts an
 * earlier message of the mail names while the newest part has lines of its own.
 */

import { Typography } from "@norican/react";
import { type KeyboardEvent } from "react";

import { quotableRows } from "@/features/case-review/bulk-quantity-dialog";
import { mainPageNotesOf, noLinesFactsOf } from "@/features/case-review/case-action";
import { stripCustomerOf } from "@/features/case-review/case-summary-strip";
import { currentDraftLineId, isRowTarget } from "@/features/case-review/draft-focus";
import { DraftList } from "@/features/case-review/draft-line-card";
import { type DraftCellContext } from "@/features/case-review/draft-line-cells";
import { type DraftRow, groupedRows, stripCounts } from "@/features/case-review/draft-line-groups";
import { DraftStrip } from "@/features/case-review/draft-strip";
import { DraftTable } from "@/features/case-review/draft-table";
import { RemovedLines } from "@/features/case-review/line-corrections";
import { caseTotals, totalsText } from "@/features/case-review/line-totals";
import { type CaseWrites, colleagueHasCase } from "@/features/case-review/use-case-writes";
import { type EarlierMention } from "@/lib/api";
import { useAreaLabels } from "@/lib/areas";
import { shortcutsOn } from "@/lib/features";
import { isOutOfScopeCase, isParkedIntent } from "@/lib/intents";
import { applyingPrice, localPriceNote } from "@/lib/local-price";
import { type QuoteCaseDetail, type QuoteLine } from "@/lib/mock";
import {
  draftCaptionText,
  EARLIER_MENTIONS_CAPTION,
  EARLIER_MENTIONS_NOTE,
  earlierMentionText,
  LINE_STATUS_WORDS,
  LONG_LIST_FOLD_AT,
  LONG_LIST_WORDS,
  quotationLineChip,
} from "@/lib/sales-flow";
import { attachmentFileText } from "@/lib/work-labels";

/**
 * The sources of a long draft (hunt round 2, decision 4): one group per sheet, file or
 * the mail text, with its count, in the order the rows first name them. Every row stays
 * in the draft; the groups say where the rows come from.
 */
export function sourceGroupsOf(rows: readonly DraftRow[]): { source: string; n: number }[] {
  const groups: { source: string; n: number }[] = [];
  for (const { line } of rows) {
    const source = line.source;
    const label =
      source?.kind === "attachment" && source.file
        ? [
            attachmentFileText({ file: source.file, inline: source.inline, child: source.child }),
            source.sheet ? `sheet ${source.sheet}` : null,
          ]
            .filter(Boolean)
            .join(", ")
        : source?.kind === "quotation"
          ? quotationLineChip(source.doc_number)
          : LONG_LIST_WORDS.mailText;
    const group = groups.find((entry) => entry.source === label);
    if (group) {
      group.n += 1;
    } else {
      groups.push({ source: label, n: 1 });
    }
  }
  return groups;
}

/** The sources of a long draft with their counts, over the rows. */
function SourceGroups({ rows }: { rows: readonly DraftRow[] }) {
  const groups = sourceGroupsOf(rows);
  return (
    <Typography
      as="div"
      variant="caption"
      tone="muted"
      className="flex flex-wrap gap-x-3 gap-y-0.5"
      data-source-groups
    >
      {groups.map((group) => (
        <span key={group.source} className="break-words" data-source-group={group.source}>
          {LONG_LIST_WORDS.group(group.source, group.n)}
        </span>
      ))}
    </Typography>
  );
}

/** The server's sentence for a mail without line items starts like this. */
const NO_LINES_SENTENCE = "No line items were found in the mail text.";

/** Why there is no draft list, in one sentence. */
export function noLinesText(detail: QuoteCaseDetail): string {
  if (detail.state === "queued" || detail.state === "processing") {
    return "I2Q is still reading this mail.";
  }
  if (detail.state === "duplicate") {
    return "Copy of a mail that is already a case.";
  }
  if (isOutOfScopeCase(detail)) {
    return isParkedIntent(detail.engineIntent ?? detail.intent)
      ? "Not an inquiry: a purchase order. Nothing is prepared for it."
      : "Not an inquiry. Nothing is prepared for it.";
  }
  if (detail.state === "not-an-rfq") {
    return "Not an inquiry after a second look. I2Q prepared no draft.";
  }
  // A suggested customer is not "not identified": the line above offers Confirm
  // (tester round 5, 12 Oct 2026).
  if (detail.customerCertainty === "to-confirm") {
    return "Customer to confirm. I2Q prepared no draft.";
  }
  if (detail.customerCertainty === "none" || detail.route === "ask-customer") {
    return "Customer not identified. I2Q prepared no draft.";
  }
  const said = (detail.whyHere ?? []).find((s) => s.startsWith(NO_LINES_SENTENCE));
  return said ?? NO_LINES_SENTENCE;
}

/**
 * The sentence under the strip of a case without rows, or null when the strip's caption
 * ("No lines found in the mail") already says it: one sentence per state, not two
 * (tester round 5, 12 Oct 2026).
 */
export function noLinesNote(detail: QuoteCaseDetail): string | null {
  const why = noLinesText(detail);
  return why.startsWith(NO_LINES_SENTENCE) ? null : why;
}

/** The two forms of the draft: the table on a laptop, the cards on a phone and in Outlook. */
export type DraftLayout = "table" | "list";

export interface DraftLinesProps {
  detail: QuoteCaseDetail;
  /** The lines to review (stand-in lines left out). */
  lines: readonly QuoteLine[];
  layout: DraftLayout;
  maySeeMargin: boolean;
  detailsOn: boolean;
  onDetailsChange: (on: boolean) => void;
  /** The line the primary action points at ("Pick 2 items"), focused in the list. */
  focusLineId: string | null;
  writes: CaseWrites;
  onPick: (lineId: string) => void;
  /** The row menu's "Set price" / "Change price" (round 4): opens the price dialog. */
  onSetPrice: (lineId: string) => void;
  /** The row menu's "Correct quantity" (U-01, 12 Oct 2026): opens the correction dialog. */
  onCorrectQty: (lineId: string) => void;
  /** A row's chevron opened its details: the line a person looks at (the mail marks it). */
  onOpenDetails?: (lineId: string) => void;
  /** A row's customer words were clicked: the mail marks them and scrolls them into view. */
  onChooseLine?: (lineId: string) => void;
  /** Opens "Set quantity for all lines"; without it the strip offers no quantity action. */
  onSetQuantityAll?: () => void;
  /** The viewer may approve a price below the limit (the row menu offers it). */
  mayApprove?: boolean;
  /**
   * Enter on a row: confirm it, or open its item search. Returns false when the row has
   * nothing to confirm or pick, and the key then stays with the grid.
   */
  onRowEnter?: (lineId: string) => boolean;
}

export function DraftLines({
  detail,
  lines,
  layout,
  maySeeMargin,
  detailsOn,
  onDetailsChange,
  focusLineId,
  writes,
  onPick,
  onSetPrice,
  onCorrectQty,
  onOpenDetails,
  onChooseLine,
  onSetQuantityAll,
  mayApprove = false,
  onRowEnter,
}: DraftLinesProps) {
  const areas = useAreaLabels();
  const caseArea = detail.area ?? null;
  const rows = groupedRows(lines, detail.lines);
  const counts = stripCounts(lines);
  const removed = lines.filter((line) => line.requestAction === "remove").length;
  const found = rows.filter(
    ({ word }) => word === LINE_STATUS_WORDS.matched || word === LINE_STATUS_WORDS.confirm,
  ).length;
  // A customer is suggested but not confirmed: a line price waits for it (no "not identified").
  const customerSuggested = detail.customerCertainty === "to-confirm";
  // Nobody is suggested (the customer line says "Find customer"): the price waits for the
  // search, the cells do not speak of a confirmation (review of the fixes, M6).
  const customerToFind =
    detail.customerCertainty !== "certain" && stripCustomerOf(detail).state === "none";
  const priceNote = (line: QuoteLine) =>
    localPriceNote({
      price: line.price,
      rules: detail.rules,
      catalogueArea: line.catalogueArea,
      caseArea,
      areaLabel: areas.label,
    });
  const longFigures = layout === "table";
  const priced = rows.some(({ line }) => line.price != null);
  const orderValue =
    detailsOn && priced
      ? totalsText(caseTotals(rows.map(({ line }) => applyingPrice(line, priceNote(line)))))
      : null;
  const ctx: DraftCellContext = {
    detail,
    caseArea,
    maySeeMargin,
    customerSuggested,
    customerToFind,
    priceNote,
    longFigures: longFigures && detailsOn,
    mayApprove,
    writes,
    onPick,
    onSetPrice,
    onCorrectQty,
    // A click on the words reveals the mail text; without a reveal handler it marks only.
    onChooseLine: (lineId) => (onChooseLine ?? onOpenDetails)?.(lineId),
  };
  // The quantity offer of the strip: an open case with two or more lines to quote. Not
  // while a colleague has the case: one click would write many lines into their case,
  // with no word about it ("Take over" comes first; one row can still be corrected).
  const quotable = quotableRows(rows);
  const workOpen = (detail.workStatus ?? "open") === "open";
  const quantityOffer =
    workOpen && onSetQuantityAll && quotable.length >= 2 && !colleagueHasCase(writes)
      ? { onSetAll: onSetQuantityAll }
      : null;

  // Enter on a row itself (a cell of the table, a card) confirms it or opens its item
  // search. The page takes the key before the DS grid does (the grid would step into the
  // control of the cell); Enter on a button or in a field stays with that control, and F2
  // still steps into a cell.
  const onRowKey = (event: KeyboardEvent<HTMLElement>) => {
    if (
      event.key !== "Enter" ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      event.repeat ||
      !onRowEnter ||
      !shortcutsOn() ||
      !isRowTarget(event.target)
    ) {
      return;
    }
    const lineId = currentDraftLineId(event.target as Element);
    if (lineId && onRowEnter(lineId)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return (
    <section
      className="flex min-w-0 flex-col gap-2"
      aria-label="Quote draft"
      data-draft-lines
      onKeyDownCapture={onRowKey}
    >
      <DraftStrip
        caption={draftCaptionText(found, rows.length, removed, noLinesFactsOf(detail))}
        counts={counts}
        hasRows={rows.length > 0}
        detailsOn={detailsOn}
        onDetailsChange={onDetailsChange}
        longFigures={longFigures}
        quantity={quantityOffer}
      />
      {/* A list I2Q did not read whole, a file it did not read: on the page, not only in
          the Details sheet (hunt round 2, Q-27). */}
      {mainPageNotesOf(detail).map((note) => (
        <Typography
          key={note.key}
          variant="caption"
          className="text-warning-fg break-words"
          data-main-page-note={note.key}
        >
          {note.text}
        </Typography>
      ))}
      {rows.length > LONG_LIST_FOLD_AT && <SourceGroups rows={rows} />}
      {rows.length === 0 ? (
        noLinesNote(detail) && (
          <Typography variant="body-sm" tone="muted" className="break-words">
            {noLinesNote(detail)}
          </Typography>
        )
      ) : layout === "table" ? (
        <DraftTable rows={rows} ctx={ctx} onOpenDetails={onOpenDetails} focusLineId={focusLineId} />
      ) : (
        <DraftList
          rows={rows}
          ctx={ctx}
          detailsOn={detailsOn}
          focusLineId={focusLineId}
          onOpenDetails={onOpenDetails}
        />
      )}
      {orderValue && (
        <Typography variant="caption" tone="muted" className="tabular-nums" data-order-value>
          {orderValue}
        </Typography>
      )}
      <RemovedLines caseId={detail.id} lines={detail.lines} />
      <EarlierMentions mentions={detail.earlierMentions ?? []} />
    </section>
  );
}

/**
 * The parts an earlier message of the mail names while the newest part has lines of its
 * own (bug hunt 14 Oct 2026, Q-01 and Q-02): a short list under the draft, display only.
 * No button makes a line of them; an empty list shows nothing.
 */
export function EarlierMentions({ mentions }: { mentions: readonly EarlierMention[] }) {
  if (mentions.length === 0) {
    return null;
  }
  return (
    <div className="flex min-w-0 flex-col gap-1" data-earlier-mentions>
      <Typography variant="caption" tone="subtle">
        {EARLIER_MENTIONS_CAPTION}
      </Typography>
      <Typography variant="caption" tone="muted" className="break-words">
        {EARLIER_MENTIONS_NOTE}
      </Typography>
      <ul className="flex min-w-0 flex-col gap-0.5">
        {mentions.map((mention, index) => (
          <li
            key={`${mention.written}-${index}`}
            className="text-fg-muted min-w-0 text-xs break-words"
          >
            {earlierMentionText(mention)}
          </li>
        ))}
      </ul>
    </div>
  );
}
